import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export const INCIDENT_KINDS = ['accident', 'vehicle_damage', 'cargo_damage', 'fine', 'complaint', 'other'] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];
export const AT_FAULT = ['yes', 'partly', 'no', 'unknown'] as const;
export type AtFault = (typeof AT_FAULT)[number];

export const INCIDENT_KIND_BG: Record<IncidentKind, string> = {
  accident: 'ПТП / катастрофа',
  vehicle_damage: 'Щета по буса',
  cargo_damage: 'Щета по товара',
  fine: 'Глоба',
  complaint: 'Оплакване от клиент',
  other: 'Друго',
};

export const AT_FAULT_BG: Record<AtFault, string> = {
  yes: 'по негова вина',
  partly: 'частично по негова вина',
  no: 'не е виновен',
  unknown: 'вината не е ясна',
};

export interface Incident {
  id: string;
  driverId: string;
  occurredOn: string;
  kind: IncidentKind;
  atFault: AtFault;
  vehicleId: string | null;
  vehicle: string | null;
  tripId: string | null;
  tripNumber: string | null;
  place: string | null;
  description: string;
  cost: number | null;
  ccy: string | null;
  reference: string | null;
  beforeEmployment: boolean;
  documentIds: string[];
  notes: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface DossierEvent {
  id: string;
  source: 'stop_damage' | 'trip_incident' | 'fine_expense' | 'infringement';
  at: string;
  title: string;
  detail: string | null;
  tripId: string | null;
  tripNumber: string | null;
  amount: number | null;
  ccy: string | null;
}

export interface Dossier {
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    phone: string | null;
    email: string | null;
    status: string;
    hiredOn: string | null;
    terminatedOn: string | null;
    homeBase: string | null;
    languages: string[];
    licenceNo: string | null;
    licenceCategories: string[];
    notes: string | null;
    hasPhoneApp: boolean;
  };
  expiries: Array<{ key: string; label: string; date: string | null; daysLeft: number | null }>;
  stats: {
    tripsTotal: number;
    tripsLastYear: number;
    kmLastYear: number;
    tripsActive: number;
    lastTripAt: string | null;
    incidentsTotal: number;
    accidents3y: number;
    accidentsAtFault3y: number;
    damageEur3y: number;
  };
  incidents: Incident[];
  events: DossierEvent[];
}

export interface IncidentInput {
  occurredOn: string;
  kind: IncidentKind;
  atFault: AtFault;
  vehicleId: string | null;
  tripId: string | null;
  place: string | null;
  description: string;
  cost: number | null;
  ccy: string;
  reference: string | null;
  beforeEmployment: boolean;
  documentIds: string[];
  notes: string | null;
}

export function useDossier(driverId: string) {
  return useQuery({
    queryKey: ['dossier', driverId],
    queryFn: () => api.get<Dossier>(`/drivers/${driverId}/dossier`),
  });
}

function useDossierMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dossier'] });
    },
  });
}

export function useSaveIncident(driverId: string) {
  return useDossierMutation((input: { id?: string; body: IncidentInput }) =>
    input.id
      ? api.patch<Incident>(`/driver-incidents/${input.id}`, input.body)
      : api.post<Incident>(`/drivers/${driverId}/incidents`, input.body),
  );
}

export function useDeleteIncident() {
  return useDossierMutation((id: string) => api.del<{ id: string }>(`/driver-incidents/${id}`));
}
